import React, { useState, useEffect, useRef } from "react";
import { Send, Paperclip, Archive, ArchiveRestore, FileText, Loader2, Download, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const bookingToken = (text) => {
  const m = (text || "").match(/reserver-creneau\/([A-Za-z0-9]+)/);
  return m ? m[1] : null;
};

const icsUrl = (text) => {
  const m = (text || "").match(/(\/api\/public\/mission-booking\/[A-Za-z0-9]+\/ics)/);
  return m ? m[1] : null;
};

const linkify = (text) => {
  const parts = (text || "").split(/(https?:\/\/[^\s]+|\/reserver-creneau\/[A-Za-z0-9]+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p) || /^\/reserver-creneau\//.test(p)) {
      return <a key={i} href={p} target={p.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="underline font-medium break-all">{p}</a>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

export default function Messagerie() {
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("active");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/conversations").then((r) => setConvos(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const visible = convos.filter((c) => (tab === "archived" ? c.archived : !c.archived));
  const active = convos.find((c) => c.id === activeId);

  useEffect(() => {
    if (active && (tab === "archived") !== !!active.archived) setActiveId(null);
    if (!active && visible.length) setActiveId(visible[0].id);
    // eslint-disable-next-line
  }, [tab, convos]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const msgText = text;
    setText("");
    setConvos((prev) => prev.map((c) => c.id === activeId
      ? { ...c, messages: [...c.messages, { from: "me", text: msgText, time: "à l'instant" }], unread: 0 }
      : c));
    try { await api.post(`/conversations/${activeId}/messages`, { text: msgText }); }
    catch (e) { /* keep optimistic message */ }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !active) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Fichier trop lourd (max 15 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", "");
      const r = await api.post(`/conversations/${activeId}/attachments`, fd);
      setConvos((prev) => prev.map((c) => c.id === activeId
        ? { ...c, messages: [...c.messages, r.data], unread: 0 } : c));
      toast.success("Pièce jointe envoyée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi du fichier impossible");
    } finally { setUploading(false); }
  };

  const toggleArchive = async (c) => {
    const next = !c.archived;
    setConvos((prev) => prev.map((x) => x.id === c.id ? { ...x, archived: next } : x));
    try {
      await api.post(`/conversations/${c.id}/archive`, { archived: next });
      toast.success(next ? "Discussion archivée" : "Discussion désarchivée");
    } catch { toast.error("Action impossible"); load(); }
  };

  const archivedCount = convos.filter((c) => c.archived).length;

  return (
    <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden h-[calc(100vh-160px)] flex">
      <div className="w-72 border-r border-brand-soft flex flex-col">
        <div className="px-5 py-4 border-b border-brand-soft">
          <h3 className="font-semibold text-brand-greenDeep">Conversations</h3>
          <div className="flex gap-1.5 mt-3">
            <button onClick={() => setTab("active")} data-testid="msg-tab-active"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === "active" ? "bg-brand-greenDeep text-white" : "bg-brand-soft text-brand-greenDeep"}`}>Actives</button>
            <button onClick={() => setTab("archived")} data-testid="msg-tab-archived"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === "archived" ? "bg-brand-greenDeep text-white" : "bg-brand-soft text-brand-greenDeep"}`}>Archivées{archivedCount ? ` (${archivedCount})` : ""}</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
          {visible.length === 0 && <p className="px-5 py-6 text-xs text-brand-green/45">{tab === "archived" ? "Aucune discussion archivée." : "Aucune discussion."}</p>}
          {visible.map((c) => {
            const last = c.messages[c.messages.length - 1];
            const preview = last?.attachment ? `📎 ${last.attachment.filename}` : last?.text;
            return (
              <div key={c.id}
                className={`group w-full flex items-center gap-3 px-5 py-4 border-b border-brand-soft transition-colors cursor-pointer ${activeId === c.id ? "bg-brand-softer" : "hover:bg-brand-softer/50"}`}
                data-testid={`conversation-item-${c.id}`}
                onClick={() => { setActiveId(c.id); setConvos(convos.map((x) => x.id === c.id ? { ...x, unread: 0 } : x)); }}>
                <span className="w-11 h-11 rounded-full bg-brand-lime flex items-center justify-center font-semibold text-brand-greenDeep flex-shrink-0">{c.avatar}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-brand-greenDeep truncate">{c.name}</p>
                  <p className="text-xs text-brand-green/50 truncate">{preview}</p>
                </div>
                {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-brand-limeDark text-brand-greenDeep text-xs font-bold flex items-center justify-center">{c.unread}</span>}
                <button onClick={(e) => { e.stopPropagation(); toggleArchive(c); }} title={c.archived ? "Désarchiver" : "Archiver"}
                  className="p-1.5 rounded-full text-brand-green/40 hover:text-brand-greenDeep hover:bg-brand-soft opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`archive-list-btn-${c.id}`}>
                  {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {active ? (
          <>
            <div className="px-6 py-4 border-b border-brand-soft flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-brand-lime flex items-center justify-center font-semibold text-brand-greenDeep">{active.avatar}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-greenDeep truncate">{active.name}</p>
                <p className="text-xs text-brand-green/50 truncate">{active.property}</p>
              </div>
              <button onClick={() => toggleArchive(active)} data-testid="archive-conv-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold hover:bg-brand-softer transition-colors">
                {active.archived ? <><ArchiveRestore className="w-4 h-4" /> Désarchiver</> : <><Archive className="w-4 h-4" /> Archiver</>}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-brand-softer/30" data-testid="messages-pane">
              {active.messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${m.from === "me" ? "bg-brand-lime text-brand-greenDeep rounded-br-md" : "bg-white border border-brand-soft text-brand-green rounded-bl-md"}`}>
                    {m.attachment && (
                      m.attachment.is_image ? (
                        <a href={fileUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`attachment-image-${i}`}>
                          <img src={fileUrl(m.attachment.url)} alt={m.attachment.filename} className="rounded-lg max-h-56 mb-1 border border-brand-soft/60" />
                        </a>
                      ) : (
                        <a href={fileUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`attachment-file-${i}`}
                          className="flex items-center gap-2 mb-1 px-3 py-2 rounded-lg bg-white/70 border border-brand-soft text-brand-greenDeep hover:bg-white transition-colors">
                          <FileText className="w-5 h-5 shrink-0" />
                          <span className="text-sm truncate max-w-[180px]">{m.attachment.filename}</span>
                          <Download className="w-4 h-4 shrink-0 opacity-60" />
                        </a>
                      )
                    )}
                    {m.text && <p className="text-sm whitespace-pre-wrap break-words">{linkify(icsUrl(m.text) ? m.text.replace(/\s*Ajoutez-le à votre agenda\s*:\s*\S+/, "") : m.text)}</p>}
                    {bookingToken(m.text) && (
                      <a href={`/reserver-creneau/${bookingToken(m.text)}`} data-testid="book-slot-btn"
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-greenDeep text-white text-xs font-semibold hover:bg-brand-green transition-colors">
                        <CalendarClock className="w-3.5 h-3.5" /> Réserver mon créneau
                      </a>
                    )}
                    {icsUrl(m.text) && (
                      <a href={fileUrl(icsUrl(m.text))} data-testid="ics-btn"
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold hover:bg-brand-softer transition-colors">
                        <CalendarClock className="w-3.5 h-3.5" /> Ajouter à mon agenda (.ics)
                      </a>
                    )}
                    <p className={`text-[10px] mt-1 ${m.from === "me" ? "text-brand-greenDeep/60" : "text-brand-green/40"}`}>{m.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-brand-soft flex items-center gap-3">
              <input ref={fileRef} type="file" className="hidden" onChange={onPickFile}
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" data-testid="attach-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Joindre un fichier"
                className="w-12 h-12 rounded-full bg-brand-softer hover:bg-brand-soft flex items-center justify-center text-brand-greenDeep transition-colors disabled:opacity-60"
                data-testid="attach-file-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez votre message..." data-testid="message-input"
                className="flex-1 px-4 py-3 rounded-full bg-brand-softer outline-none text-sm focus:ring-2 focus:ring-brand-lime/40 transition-all" />
              <button onClick={send} data-testid="message-send-btn"
                className="w-12 h-12 rounded-full bg-brand-lime hover:bg-brand-limeDark flex items-center justify-center text-brand-greenDeep transition-colors">
                <Send className="w-5 h-5" />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-brand-green/50">Aucune conversation</div>
        )}
      </div>
    </div>
  );
}
